import vibeCodeJson from "@/data/s2pElevateVibeCode.json";
import {
  CLAUDE_WORKSPACE_STEPS,
  EXECUTION_BRIEF,
  MCP_BOUNDARY_PROMPT,
  META_PROMPT_STARTER,
  PROJECT_MEMORY_TEMPLATE,
  VERIFICATION_PROMPT,
  VISUAL_REVIEW_PROMPT,
} from "@/data/claudeWorkspace";
import {
  createS2PElevateProgram,
  type S2PElevateCurriculum,
  type S2PElevateSessionId,
  type S2PElevateSlide,
} from "@/data/s2pElevateAI";

const MATERIAL_IDS = {
  agents: "AIB-001-MAT001",
  metaPrompting: "AIB-001-MAT002",
  vibeCoding: "AIB-001-MAT003",
  elevateBuild: "AIB-001-MAT004",
} as const;

export const AI_BUILDER_COURSE_PACKAGE = {
  id: "AIB-001",
  version: "1.0",
  title: "The AI Builder Playbook",
  subtitle: "From Job Brief to Verified Prototype",
  purpose:
    "Give leaders one end-to-end method for turning a bounded work opportunity into a prototype they can test, explain, and hand off without hiding the specification, changes, risks, or human decisions.",
  audience: {
    learner: "Non-technical and developing technical leaders who have identified a useful AI-supported Job and are ready to build a bounded prototype.",
    level: "Developing",
    priorKnowledge: "A selected Job, a visible Definition of Done, and basic familiarity with an AI assistant.",
  },
  delivery: {
    primary: "Self-paced",
    alternatives: ["Live virtual workshop", "Blended"],
    minutes: 90,
    order: "Linear",
    progression: "Open",
  },
  learningOutcome:
    "Explain how specification, workspace boundaries, meta-prompting, reviewable build loops, and evidence-led verification fit together.",
  capabilityOutcome:
    "Produce a bounded prototype and a human-reviewable evidence package that supports a decision to revise, continue in a sandbox, hand to engineering, or stop.",
  approval: {
    status: "Approved for implementation",
    basis: "The course owner approved the proposed consolidated structure and evidence package in conversation.",
    date: "2026-08-21",
  },
  modules: [
    {
      id: "AIB-001-M001",
      title: "Frame the build",
      minutes: 15,
      outcomes: ["AIB-001-O001", "AIB-001-O002"],
      concepts: ["AIB-001-C001", "AIB-001-C002", "AIB-001-C003"],
      produces: "AIB-001-A001",
      coachBrief: "Ask the learner to make the user, happy path, boundaries, and observable finish line specific. Do not write the brief for them.",
    },
    {
      id: "AIB-001-M002",
      title: "Create the safety net",
      minutes: 20,
      prerequisites: ["AIB-001-M001"],
      outcomes: ["AIB-001-O003", "AIB-001-O004"],
      concepts: ["AIB-001-C004", "AIB-001-C005", "AIB-001-C006"],
      produces: "AIB-001-A002",
      coachBrief: "Question the learner's trust boundary, reversibility, data choice, and approval limits. Redirect technical setup questions to the supplied steps.",
    },
    {
      id: "AIB-001-M003",
      title: "Shape before building",
      minutes: 20,
      prerequisites: ["AIB-001-M001", "AIB-001-M002"],
      outcomes: ["AIB-001-O005", "AIB-001-O006"],
      concepts: ["AIB-001-C007", "AIB-001-C008"],
      produces: "AIB-001-A003",
      coachBrief: "Challenge missing assumptions and vague verification. Let the learner decide when the plan is specific enough to approve.",
    },
    {
      id: "AIB-001-M004",
      title: "Build in reviewable loops",
      minutes: 20,
      prerequisites: ["AIB-001-M003"],
      outcomes: ["AIB-001-O007", "AIB-001-O008"],
      concepts: ["AIB-001-C009", "AIB-001-C010", "AIB-001-C011"],
      produces: "AIB-001-A004",
      coachBrief: "Ask what changed, what evidence passed, and what failed. Keep the learner focused on one coherent slice at a time.",
    },
    {
      id: "AIB-001-M005",
      title: "Verify and hand off",
      minutes: 15,
      prerequisites: ["AIB-001-M004"],
      outcomes: ["AIB-001-O009", "AIB-001-O010"],
      concepts: ["AIB-001-C010", "AIB-001-C012"],
      produces: "AIB-001-A005",
      coachBrief: "Test the evidence and ownership decision. Do not imply that a working prototype is production approval.",
    },
  ],
  outcomes: [
    {
      id: "AIB-001-O001",
      statement: "Convert a selected Job into a product brief that names the user, trigger, happy path, inputs, outputs, and boundaries.",
      module: "AIB-001-M001",
      purposeWhy: "A clear brief prevents the AI from silently deciding who the build serves or what progress means.",
      situationalWhys: ["Use it when an idea is still framed as a feature.", "Use it when several stakeholders describe different finish lines."],
      materials: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
    },
    {
      id: "AIB-001-O002",
      statement: "Write acceptance criteria with an observable result and failure boundary.",
      module: "AIB-001-M001",
      purposeWhy: "Observable criteria turn completion from an AI claim into something a person can test.",
      situationalWhys: ["Use them before a coding assistant proposes an implementation.", "Use them when a demo can look correct while failing an important edge case."],
      materials: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
    },
    {
      id: "AIB-001-O003",
      statement: "Establish an isolated, recoverable workspace before AI changes files.",
      module: "AIB-001-M002",
      purposeWhy: "Isolation and recovery make experimentation inspectable and reversible.",
      situationalWhys: ["Use them before opening an AI coding session in an existing project.", "Use them when the project contains production files, private data, or unrelated work."],
      materials: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
    },
    {
      id: "AIB-001-O004",
      statement: "Define durable project instructions, permissions, data limits, and human approval gates.",
      module: "AIB-001-M002",
      purposeWhy: "Durable instructions keep safety and quality expectations from depending on one chat message.",
      situationalWhys: ["Use them when a project will span multiple sessions.", "Use them when an external connection or irreversible action needs explicit approval."],
      materials: [MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
    },
    {
      id: "AIB-001-O005",
      statement: "Use a meta-prompt to expose assumptions and material clarifying questions before implementation.",
      module: "AIB-001-M003",
      purposeWhy: "Separating prompt design from execution makes important ambiguity visible while changes are still cheap.",
      situationalWhys: ["Use it when the request contains an outcome but not a reliable method.", "Use it when the workflow crosses tools, data sources, or approval boundaries."],
      materials: [MATERIAL_IDS.metaPrompting, MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
    },
    {
      id: "AIB-001-O006",
      statement: "Approve a build plan only when each step has a bounded scope, evidence check, and stop condition.",
      module: "AIB-001-M003",
      purposeWhy: "A bounded plan lets the learner judge the work before authorizing implementation.",
      situationalWhys: ["Use it before a multi-file or multi-step change.", "Use it when the assistant could choose an architecture, permission, or scope the learner has not approved."],
      materials: [MATERIAL_IDS.metaPrompting, MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
    },
    {
      id: "AIB-001-O007",
      statement: "Execute one coherent slice and inspect both the resulting behavior and the change that produced it.",
      module: "AIB-001-M004",
      purposeWhy: "Short loops make drift, cost, and unintended changes easier to detect and correct.",
      situationalWhys: ["Use them when a plan contains several independently testable results.", "Use them when the visible output could hide unrelated or risky changes."],
      materials: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
    },
    {
      id: "AIB-001-O008",
      statement: "Turn a failure into a reproducible test, isolate the cause, and prove the repair against the full acceptance set.",
      module: "AIB-001-M004",
      purposeWhy: "A reproducible failure prevents trial-and-error fixes from creating new uncertainty.",
      situationalWhys: ["Use it when the happy path works but an edge case fails.", "Use it when the assistant proposes a broad refactor before explaining the smallest cause."],
      materials: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.agents],
    },
    {
      id: "AIB-001-O009",
      statement: "Verify the prototype against the brief, tests, security boundary, and intended workspace state.",
      module: "AIB-001-M005",
      purposeWhy: "Verification replaces confidence in the final message with evidence a reviewer can inspect.",
      situationalWhys: ["Use it before committing, sharing, or demonstrating the prototype.", "Use it when a working demo may still expose data, credentials, permissions, or unsafe failure behavior."],
      materials: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
    },
    {
      id: "AIB-001-O010",
      statement: "Package the prototype so another person can review it and choose its next state.",
      module: "AIB-001-M005",
      purposeWhy: "A handoff proves the work can leave the builder's head without implying production approval.",
      situationalWhys: ["Use it when another person must review or continue the build.", "Use it when the correct next state may be revise, sandbox, engineering handoff, or stop."],
      materials: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
    },
  ],
  concepts: [
    { id: "AIB-001-C001", name: "Job Brief", introducedIn: "AIB-001-M001", appliedIn: ["AIB-001-M003", "AIB-001-M005"] },
    { id: "AIB-001-C002", name: "Product Brief", introducedIn: "AIB-001-M001", appliedIn: ["AIB-001-M003", "AIB-001-M005"] },
    { id: "AIB-001-C003", name: "Acceptance Criteria", introducedIn: "AIB-001-M001", appliedIn: ["AIB-001-M004", "AIB-001-M005"] },
    { id: "AIB-001-C004", name: "Trust Boundary", introducedIn: "AIB-001-M002", appliedIn: ["AIB-001-M004", "AIB-001-M005"] },
    { id: "AIB-001-C005", name: "Version History", introducedIn: "AIB-001-M002", appliedIn: ["AIB-001-M004", "AIB-001-M005"] },
    { id: "AIB-001-C006", name: "Project Memory", introducedIn: "AIB-001-M002", appliedIn: ["AIB-001-M003", "AIB-001-M004"] },
    { id: "AIB-001-C007", name: "Meta-prompt", introducedIn: "AIB-001-M003", appliedIn: ["AIB-001-M004", "AIB-001-M005"] },
    { id: "AIB-001-C008", name: "Approved Plan", introducedIn: "AIB-001-M003", appliedIn: ["AIB-001-M004", "AIB-001-M005"] },
    { id: "AIB-001-C009", name: "Reviewable Slice", introducedIn: "AIB-001-M004", appliedIn: ["AIB-001-M005"] },
    { id: "AIB-001-C010", name: "Verification Evidence", introducedIn: "AIB-001-M004", deepenedIn: "AIB-001-M005" },
    { id: "AIB-001-C011", name: "Failure Test", introducedIn: "AIB-001-M004", appliedIn: ["AIB-001-M005"] },
    { id: "AIB-001-C012", name: "Production Boundary", introducedIn: "AIB-001-M002", deepenedIn: "AIB-001-M005" },
  ],
  artifacts: [
    { id: "AIB-001-A001", name: "Product brief and acceptance criteria", producedIn: "AIB-001-M001", reusedIn: ["AIB-001-M003", "AIB-001-M005"] },
    { id: "AIB-001-A002", name: "Workspace boundary and recovery evidence", producedIn: "AIB-001-M002", reusedIn: ["AIB-001-M004", "AIB-001-M005"] },
    { id: "AIB-001-A003", name: "Meta-prompt and approved plan", producedIn: "AIB-001-M003", reusedIn: ["AIB-001-M004", "AIB-001-M005"] },
    { id: "AIB-001-A004", name: "Working slice and test evidence", producedIn: "AIB-001-M004", reusedIn: ["AIB-001-M005"] },
    { id: "AIB-001-A005", name: "Verified Prototype Package", producedIn: "AIB-001-M005", terminal: true },
  ],
  materials: [
    { id: MATERIAL_IDS.agents, name: "Claude Agents Playbook", role: "Safe workspace, project instructions, bounded execution, context, and verification" },
    { id: MATERIAL_IDS.metaPrompting, name: "Meta-Prompting Starter Pack", role: "Clarifying questions, reusable briefs, and atomic plans" },
    { id: MATERIAL_IDS.vibeCoding, name: "Vibe Code Like an AI Engineer", role: "Product brief, acceptance criteria, debugging, security review, and handoff" },
    { id: MATERIAL_IDS.elevateBuild, name: "Serve2Perform ELEVATE Build module", role: "Job, Definition of Done, literal stop rules, sandbox validation, and human review" },
  ],
  assessment: {
    id: "AIB-001-AS001",
    type: "Human-reviewed portfolio",
    artifact: "AIB-001-A005",
    status: "Formative until reviewed by a person",
    evaluatesOutcomes: [
      "AIB-001-O001", "AIB-001-O002", "AIB-001-O003", "AIB-001-O004", "AIB-001-O005",
      "AIB-001-O006", "AIB-001-O007", "AIB-001-O008", "AIB-001-O009", "AIB-001-O010",
    ],
    requiredEvidence: [
      "Product brief and acceptance criteria",
      "Meta-prompt and approved implementation plan",
      "Recoverable workspace with explicit boundaries",
      "Working prototype with happy-path and failure-path evidence",
      "Change summary, known limitations, security review, and named next-state owner",
    ],
  },
} as const;

const vibeCurriculum = vibeCodeJson as S2PElevateCurriculum;
const vibeSlidesById = new Map(vibeCurriculum.slides.map((slide) => [slide.id, slide]));
const workspaceStepsById = new Map(CLAUDE_WORKSPACE_STEPS.map((step) => [step.id, step]));

const workspaceStep = (id: string) => {
  const step = workspaceStepsById.get(id);
  if (!step) throw new Error(`Missing Claude workspace source step: ${id}`);
  return step;
};

const reuseVibeSlide = (
  id: string,
  override: Partial<S2PElevateSlide> & Pick<S2PElevateSlide, "session">,
): S2PElevateSlide => {
  const source = vibeSlidesById.get(id);
  if (!source) throw new Error(`Missing vibe-coding source slide: ${id}`);
  return {
    ...source,
    ...override,
    sourceIds: [...new Set([...source.sourceIds, MATERIAL_IDS.vibeCoding, ...(override.sourceIds ?? [])])],
  };
};

const stepSlide = (
  id: string,
  session: S2PElevateSessionId,
  options: Partial<S2PElevateSlide> = {},
): S2PElevateSlide => {
  const stepId = id.replace(/^step-/, "");
  const step = workspaceStep(stepId);
  return {
    id,
    session,
    kind: "activity",
    tone: "cloud",
    eyebrow: step.duration,
    title: step.title,
    lead: step.whyItMatters,
    bullets: step.actions,
    note: step.checkpoint,
    sourceIds: [MATERIAL_IDS.agents],
    ...options,
  };
};

const slides: S2PElevateSlide[] = [
  {
    id: "builder-welcome",
    session: 1,
    kind: "cover",
    tone: "navy",
    eyebrow: "One build lifecycle · Five modules · 90 minutes",
    title: "The AI Builder Playbook",
    lead: "Turn one Job Brief into a prototype you can test, explain, and hand off without hiding the specification, changes, risks, or human decisions.",
    sourceIds: Object.values(MATERIAL_IDS),
  },
  {
    id: "builder-outcomes",
    session: 1,
    kind: "outcomes",
    tone: "paper",
    eyebrow: "The course outcome",
    title: "Build one bounded prototype and bring the proof",
    bullets: [
      "A product brief with an observable finish line and failure boundary.",
      "A recoverable workspace with explicit permissions, data limits, and approval gates.",
      "A meta-prompt and approved plan that make assumptions visible before implementation.",
      "A working slice with happy-path and failure-path test evidence.",
      "A handoff package that supports a clear next-state decision.",
    ],
    sourceIds: Object.values(MATERIAL_IDS),
  },
  {
    id: "builder-roadmap",
    session: 1,
    kind: "concept",
    tone: "cloud",
    eyebrow: "The full operating loop",
    title: "Frame. Protect. Shape. Build. Prove.",
    sections: [
      { label: "01", title: "Frame", body: "Turn the Job into a product brief and acceptance criteria." },
      { label: "02", title: "Protect", body: "Create a safe, isolated, and recoverable workspace." },
      { label: "03", title: "Shape", body: "Use meta-prompting and planning before implementation." },
      { label: "04", title: "Build", body: "Execute, inspect, and debug one reviewable slice at a time." },
      { label: "05", title: "Prove", body: "Verify, review the security boundary, and hand off the evidence." },
    ],
    sourceIds: Object.values(MATERIAL_IDS),
  },
  {
    id: "frame-the-build",
    session: 1,
    kind: "session",
    tone: "blue",
    eyebrow: "Module 1 of 5 · Frame",
    title: "Frame the build",
    lead: "A prototype is useful only when the user, result, boundaries, and evidence are specific before AI starts changing anything.",
    bullets: ["Start with the Job, not the interface.", "Define the smallest useful happy path.", "Make success and failure observable."],
    sourceIds: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
  },
  {
    id: "why-frame-first",
    session: 1,
    kind: "compare",
    tone: "navy",
    eyebrow: "Why this comes first",
    title: "Speed without a finish line creates expensive ambiguity",
    sections: [
      { label: "Unframed", title: "Generate and react", body: "The learner judges whatever appears, after the AI has already chosen assumptions and scope." },
      { label: "Framed", title: "Specify and verify", body: "The learner decides what progress means, what stays out, and what evidence must exist." },
    ],
    note: "Slow down while changes are cheap. The clearer What makes the How smaller.",
    sourceIds: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
  },
  reuseVibeSlide("job-brief-to-product-brief", {
    session: 1,
    eyebrow: "Define the product brief",
    sourceIds: [MATERIAL_IDS.elevateBuild],
  }),
  reuseVibeSlide("prototype-acceptance-criteria", {
    session: 1,
    eyebrow: "Make the finish line observable",
    sourceIds: [MATERIAL_IDS.elevateBuild],
  }),
  {
    id: "frame-evidence",
    session: 1,
    kind: "outcomes",
    tone: "paper",
    eyebrow: "Module 1 quick win",
    title: "Your first artifact is the build contract",
    lead: "Do not continue until another person can read the brief and identify the expected result, the failure boundary, and what remains out of scope.",
    bullets: ["User and trigger", "Happy path", "Inputs and outputs", "Boundaries and human decisions", "Acceptance criteria"],
    sourceIds: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
  },
  {
    id: "create-the-safety-net",
    session: 2,
    kind: "session",
    tone: "blue",
    eyebrow: "Module 2 of 5 · Protect",
    title: "Create the safety net",
    lead: "Give the AI enough room to work while keeping unrelated files, real data, credentials, and irreversible actions outside the trust boundary.",
    bullets: ["Choose one low-risk project folder.", "Record a recoverable starting point.", "Write the rules that should survive every session."],
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
  },
  reuseVibeSlide("safe-coding-workspace", {
    session: 2,
    eyebrow: "Protect the real system",
    sourceIds: [MATERIAL_IDS.agents],
  }),
  stepSlide("step-open-project", 2, {
    eyebrow: "Safety 01 · Workspace boundary",
    title: "Open only the project you intend AI to touch",
  }),
  stepSlide("step-git-safety-net", 2, {
    eyebrow: "Safety 02 · Reversibility",
    title: "Record the starting state before anything changes",
  }),
  stepSlide("step-start-safely", 2, {
    eyebrow: "Safety 03 · Permissions",
    title: "Start with read-only orientation and explicit approval gates",
  }),
  stepSlide("step-project-memory", 2, {
    kind: "prompt",
    tone: "navy",
    eyebrow: "Safety 04 · Durable instructions",
    title: "Put the working agreements in project memory",
    bullets: undefined,
    promptLabel: "Starter project instructions",
    prompt: PROJECT_MEMORY_TEMPLATE,
  }),
  {
    id: "safety-evidence",
    session: 2,
    kind: "outcomes",
    tone: "paper",
    eyebrow: "Module 2 quick win",
    title: "Prove the workspace is bounded and recoverable",
    bullets: [
      "The terminal or workspace points at the intended project only.",
      "The starting state is visible in version history.",
      "Real credentials and private data are absent.",
      "Project instructions name commands, constraints, and approval gates.",
    ],
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
  },
  {
    id: "shape-before-building",
    session: 3,
    kind: "session",
    tone: "blue",
    eyebrow: "Module 3 of 5 · Shape",
    title: "Shape before building",
    lead: "Separate designing the work from executing it so assumptions, risks, and verification can be reviewed before the prototype spends time or changes files.",
    bullets: ["Ask the meta-prompt.", "Answer only material clarifying questions.", "Approve a plan with evidence and stop conditions."],
    sourceIds: [MATERIAL_IDS.metaPrompting, MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
  },
  {
    id: "meta-prompt-model",
    session: 3,
    kind: "compare",
    tone: "cloud",
    eyebrow: "The separation that prevents drift",
    title: "Design the brief before the build begins",
    sections: [
      { label: "Meta-prompt", title: "Shape the work", body: "Ask AI to expose missing decisions, improve the specification, and create a reusable brief." },
      { label: "Execution", title: "Do the work", body: "Give a new session the approved brief and let it implement within the agreed scope and controls." },
    ],
    sourceIds: [MATERIAL_IDS.metaPrompting, MATERIAL_IDS.agents],
  },
  stepSlide("step-install-toolkit", 3, {
    eyebrow: "Optional pre-work · Install once",
    title: "Install the prompt and planning toolkit",
    lead: "The toolkit is optional. You can use the copyable prompts in this training without installing it, or install the reusable commands for future projects.",
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.metaPrompting],
    sourceUrl: "/claude-meta-prompting.zip",
    sourceLabel: "Download the toolkit",
  }),
  stepSlide("step-shape-the-work", 3, {
    kind: "prompt",
    tone: "navy",
    eyebrow: "Shape 01 · Ask before implementing",
    title: "Turn the idea into a brief you can inspect",
    bullets: undefined,
    promptLabel: "Start the meta-prompt",
    prompt: META_PROMPT_STARTER,
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.metaPrompting, MATERIAL_IDS.elevateBuild],
  }),
  reuseVibeSlide("ask-for-build-plan", {
    session: 3,
    eyebrow: "Shape 02 · Approve the plan",
    title: "Require the build path, evidence, risks, and stop conditions",
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.metaPrompting, MATERIAL_IDS.elevateBuild],
  }),
  {
    id: "shape-evidence",
    session: 3,
    kind: "outcomes",
    tone: "paper",
    eyebrow: "Module 3 quick win",
    title: "Approve the plan only when completion can be proven",
    bullets: ["The brief names the outcome and non-goals.", "Material questions have concrete answers.", "Each plan step has an evidence check.", "Destructive or ambiguous decisions have stop conditions."],
    sourceIds: [MATERIAL_IDS.metaPrompting, MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
  },
  {
    id: "build-in-reviewable-loops",
    session: 4,
    kind: "session",
    tone: "blue",
    eyebrow: "Module 4 of 5 · Build",
    title: "Build in reviewable loops",
    lead: "A plan is not permission to disappear into a black box. Build one coherent slice, inspect it, and earn the next slice with evidence.",
    bullets: ["Implement one observable result.", "Inspect behavior and the change behind it.", "Turn failures into reproducible tests."],
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
  },
  stepSlide("step-execute-and-steer", 4, {
    kind: "prompt",
    tone: "navy",
    eyebrow: "Build 01 · Bound the loop",
    title: "Execute one coherent slice at a time",
    bullets: undefined,
    promptLabel: "Execution brief",
    prompt: EXECUTION_BRIEF,
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
  }),
  reuseVibeSlide("inspect-the-change", {
    session: 4,
    eyebrow: "Build 02 · Inspect both layers",
    sourceIds: [MATERIAL_IDS.agents],
  }),
  reuseVibeSlide("test-debug-loop", {
    session: 4,
    eyebrow: "Build 03 · Reproduce before repairing",
    sourceIds: [MATERIAL_IDS.agents],
  }),
  stepSlide("step-add-context", 4, {
    kind: "prompt",
    tone: "cloud",
    eyebrow: "Optional · Add evidence, not noise",
    title: "Add visual context or one narrow connection only when needed",
    bullets: undefined,
    promptLabel: "Choose the prompt that closes the named gap",
    prompt: `${VISUAL_REVIEW_PROMPT}\n\n--- OR, FOR ONE MCP CONNECTION ---\n\n${MCP_BOUNDARY_PROMPT}`,
  }),
  {
    id: "build-evidence",
    session: 4,
    kind: "outcomes",
    tone: "paper",
    eyebrow: "Module 4 quick win",
    title: "One meaningful slice works before the next begins",
    bullets: ["The intended behavior is visible.", "The diff or change summary is understandable.", "The narrow relevant check passes.", "One failure is captured and tested before repair."],
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
  },
  {
    id: "verify-and-handoff",
    session: 5,
    kind: "session",
    tone: "blue",
    eyebrow: "Module 5 of 5 · Prove",
    title: "Verify and hand off",
    lead: "The prototype is not finished when the AI says it is finished. It is finished when the evidence, limits, owner, and next-state decision are visible.",
    bullets: ["Run the agreed checks.", "Review the security and production boundary.", "Package the result so another person can continue."],
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
  },
  stepSlide("step-verify-and-finish", 5, {
    kind: "prompt",
    tone: "navy",
    eyebrow: "Prove 01 · Evidence before claims",
    title: "Review the work before committing or sharing it",
    bullets: undefined,
    promptLabel: "Pre-handoff verification",
    prompt: VERIFICATION_PROMPT,
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.vibeCoding],
  }),
  reuseVibeSlide("prototype-security-review", {
    session: 5,
    eyebrow: "Prove 02 · Production boundary",
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
  }),
  reuseVibeSlide("prototype-handoff", {
    session: 5,
    eyebrow: "Prove 03 · Transfer the context",
    sourceIds: [MATERIAL_IDS.agents, MATERIAL_IDS.elevateBuild],
  }),
  {
    id: "verified-prototype-package",
    session: 5,
    kind: "outcomes",
    tone: "paper",
    eyebrow: "Course capstone · Human review required",
    title: "Assemble the Verified Prototype Package",
    bullets: [...AI_BUILDER_COURSE_PACKAGE.assessment.requiredEvidence],
    note: "Saved progress and self-checks are formative. A person reviews the package before anyone treats the prototype as approved or production-ready.",
    sourceIds: Object.values(MATERIAL_IDS),
  },
  {
    id: "prototype-next-state",
    session: 5,
    kind: "discussion",
    tone: "cloud",
    eyebrow: "The decision point",
    title: "What should happen to this prototype next?",
    questions: [
      "Which acceptance criterion has the strongest evidence, and which remains uncertain?",
      "What did the failure test or change review reveal that the visible demo did not?",
      "Who owns the unresolved security, data, reliability, or workflow decision?",
      "Should this be revised, continued in the sandbox, handed to engineering, or stopped?",
    ],
    sourceIds: [MATERIAL_IDS.vibeCoding, MATERIAL_IDS.elevateBuild],
  },
  {
    id: "builder-complete",
    session: 5,
    kind: "finish",
    tone: "navy",
    eyebrow: "The AI Builder Playbook complete",
    title: "You built fast and kept the work accountable",
    lead: "Keep the prototype inside its agreed boundary until the evidence, owner, security review, and path to production are clear.",
    bullets: ["Frame the Job.", "Protect the workspace.", "Shape the plan.", "Build and debug in loops.", "Prove and hand off."],
    sourceIds: Object.values(MATERIAL_IDS),
  },
];

export const AI_BUILDER_CURRICULUM: S2PElevateCurriculum = {
  course: {
    title: AI_BUILDER_COURSE_PACKAGE.title,
    subtitle: AI_BUILDER_COURSE_PACKAGE.subtitle,
    totalMinutes: AI_BUILDER_COURSE_PACKAGE.delivery.minutes,
  },
  sessions: [
    { id: 1, label: "Frame", title: "Frame the build", minutes: 15, coreMinutes: 7, exerciseMinutes: 6, discussionMinutes: 2, outcome: "A product brief with explicit boundaries and observable acceptance criteria." },
    { id: 2, label: "Protect", title: "Create the safety net", minutes: 20, coreMinutes: 10, exerciseMinutes: 8, discussionMinutes: 2, outcome: "An isolated, recoverable workspace with durable instructions and explicit approval gates." },
    { id: 3, label: "Shape", title: "Shape before building", minutes: 20, coreMinutes: 9, exerciseMinutes: 9, discussionMinutes: 2, outcome: "A meta-prompt and approved plan with evidence checks and stop conditions." },
    { id: 4, label: "Build", title: "Build in reviewable loops", minutes: 20, coreMinutes: 8, exerciseMinutes: 10, discussionMinutes: 2, outcome: "One working slice with an understandable change and reproducible failure test." },
    { id: 5, label: "Prove", title: "Verify and hand off", minutes: 15, coreMinutes: 6, exerciseMinutes: 7, discussionMinutes: 2, outcome: "A Verified Prototype Package and a named next-state decision." },
  ],
  checkpoints: [
    { id: "frame-brief", session: 1, title: "Product brief and acceptance criteria" },
    { id: "safe-workspace", session: 2, title: "Workspace boundary and recovery evidence" },
    { id: "approved-plan", session: 3, title: "Meta-prompt and approved plan" },
    { id: "reviewable-slice", session: 4, title: "Working slice and test evidence" },
    { id: "verified-handoff", session: 5, title: "Verified Prototype Package" },
  ],
  showcase: [],
  slides,
};

export const AI_BUILDER_PROGRAM = createS2PElevateProgram(AI_BUILDER_CURRICULUM);
